/**
 * Renders a concrete dispatch and delivery estimate on the product page.
 *
 * Dates are computed in the browser rather than in Liquid because storefront
 * pages are cached - a server-rendered date would go stale and start telling
 * shoppers the wrong thing.
 *
 * Working days only: Saturday and Sunday are skipped for both dispatch and
 * transit, matching how orders are actually processed.
 */

const WEEKEND = [0, 6];

/**
 * Adds a number of working days to a date, skipping weekends.
 * @param {Date} from
 * @param {number} days
 * @returns {Date}
 */
function addWorkingDays(from, days) {
  const date = new Date(from.getTime());
  let remaining = days;

  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    if (!WEEKEND.includes(date.getDay())) remaining -= 1;
  }

  // If we landed on a weekend with zero days to add, roll to Monday.
  while (WEEKEND.includes(date.getDay())) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

/**
 * @param {Date} date
 * @param {string} locale
 */
function formatDate(date, locale) {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
}

class HohDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    try {
      this.render();
    } catch (error) {
      // A broken estimate is worse than none - leave the static fallback in place.
      this.hidden = true;
    }
  }

  render() {
    const cutoffHour = Number(this.dataset.cutoffHour ?? 14);
    const dispatchMin = Number(this.dataset.dispatchMin ?? 1);
    const dispatchMax = Number(this.dataset.dispatchMax ?? 2);
    const deliveryMin = Number(this.dataset.deliveryMin ?? 2);
    const deliveryMax = Number(this.dataset.deliveryMax ?? 4);
    const locale = this.dataset.locale || 'en-GB';

    const now = new Date();
    const beforeCutoff = now.getHours() < cutoffHour;
    const isWorkingDay = !WEEKEND.includes(now.getDay());

    // Ordering after the cutoff (or at a weekend) pushes processing to the next working day.
    const startOffset = beforeCutoff && isWorkingDay ? 0 : 1;
    const base = startOffset === 0 ? now : addWorkingDays(now, 1);

    const dispatchFrom = addWorkingDays(base, Math.max(dispatchMin - 1, 0));
    const earliest = addWorkingDays(dispatchFrom, deliveryMin);
    const latest = addWorkingDays(addWorkingDays(base, Math.max(dispatchMax - 1, 0)), deliveryMax);

    const cutoffEl = this.querySelector('[data-cutoff]');
    const rangeEl = this.querySelector('[data-range]');

    if (cutoffEl) {
      const cutoffLabel = new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true })
        .format(new Date(new Date().setHours(cutoffHour, 0, 0, 0)))
        .replace(/\s/g, '')
        .toLowerCase();

      cutoffEl.textContent =
        beforeCutoff && isWorkingDay
          ? `Order within today's ${cutoffLabel} cut-off`
          : `Orders now process from ${formatDate(base, locale)}`;
    }

    if (rangeEl) {
      const sameDay = formatDate(earliest, locale) === formatDate(latest, locale);
      rangeEl.textContent = sameDay
        ? `Estimated delivery ${formatDate(earliest, locale)}`
        : `Estimated delivery ${formatDate(earliest, locale)} - ${formatDate(latest, locale)}`;
    }

    this.removeAttribute('data-pending');
  }
}

if (!customElements.get('hoh-delivery-estimate')) {
  customElements.define('hoh-delivery-estimate', HohDeliveryEstimate);
}
